import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ProductContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/products`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      
      const data = await response.json();
      setProducts(data);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const getProductById = useCallback((id) => {
    return products.find(product => product.id === parseInt(id));
  }, [products]);

  const getProductsByCategory = useCallback((category) => {
    if (!category || category === 'All') return products;
    return products.filter(product => product.category === category);
  }, [products]);

  const getCategories = useCallback(() => {
    const categories = [...new Set(products.map(product => product.category))];
    return ['All', ...categories];
  }, [products]);

  const decrementStock = useCallback(async (productId, size, quantity) => {
    try {
      const response = await fetch(`${API_URL}/api/products/${productId}/decrement-stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ size, quantity })
      });

      if (!response.ok) {
        throw new Error('Failed to update stock');
      }

      // Refetch products to get updated stock
      await fetchProducts();
      return true;
    } catch (err) {
      console.error('Error decrementing stock:', err);
      return false;
    }
  }, [fetchProducts]);

  const refreshProducts = useCallback(() => {
    fetchProducts();
  }, [fetchProducts]);

  return (
    <ProductContext.Provider value={{
      products,
      loading,
      error,
      getProductById,
      getProductsByCategory,
      getCategories,
      decrementStock,
      refreshProducts
    }}>
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export default ProductContext;
